import React, {useContext, useEffect, useState} from 'react';
import {TitleContext} from 'components/TitleContext';
import {useSelector, useDispatch} from 'react-redux';
import {RootState, AppDispatch} from 'store/store';
import {useParams} from 'react-router-dom';
import {ProgramsThunks} from 'features/programs/programsThunks';
import {DocumentTemplateThunks} from 'features/documentTemplate/documentTemplateThunks';
import Skeleton from 'react-loading-skeleton';
import BillingSolutionTable from 'features/programs/program/billingSolution/BillingSolutionTable';
import Drawer from 'components/Drawer';
import BillingSolutionDrawer from 'features/programs/program/billingSolution/BillingSolutionDrawer';
import DocumentTemplateElementTable from 'features/programs/program/documentTemplateElement/DocumentTemplateElementTable';
import DocumentTemplateElementDrawer from 'features/programs/program/documentTemplateElement/DocumentTemplateElementDrawer';
import ServicesTable from '../../features/programs/program/services/ServicesTable';
import ServiceDrawer from '../../features/programs/program/services/ServiceDrawer';
import BillingRulesTable from '../../features/programs/program/billingRules/BillingRulesTable';
import BillingRuleDrawer from '../../features/programs/program/billingRules/BillingRuleDrawer';
import DocumentTemplateProgramTable from '../../features/programs/program/documentTemplateProgram/DocumentTemplateProgramTable';
import DocumentTemplateProgramDrawer from '../../features/programs/program/documentTemplateProgram/DocumentTemplateProgramDrawer';
import ProgramCardsTable from '../../features/programs/program/cards/ProgramCardsTable';
import DocumentTemplateFormPage from '../DocumentTemplate/DocumentTemplateFormPage';
import {
  setActiveTab,
  PROGRAM_TABS,
} from '../../features/programs/programsSlice';

const ProgramField: React.FC<{
  label: string;
  value?: React.ReactNode;
  loading: boolean;
  lines?: number;
  width?: number | string;
}> = ({label, value, loading, lines = 1, width = 220}) => (
  <div>
    <p className="text-sm text-slate-400 dark:text-neutral-400">{label}</p>
    <div className="font-medium text-slate-600">
      {loading ? (
        <Skeleton width={width} count={lines} />
      ) : (
        value ?? <span className="text-slate-400">—</span>
      )}
    </div>
  </div>
);

const getTabClass = (tab: string, activeTab: string) =>
  [
    'me-2 cursor-pointer inline-block p-2 border-b-2 text-slate-400 whitespace-nowrap',
    activeTab === tab
      ? 'border-cyan-400 text-slate-600 dark:text-gray-200 dark:border-cyan-400'
      : 'border-transparent',
    'rounded-t-lg hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300',
  ].join(' ');

const getAriaCurrent = (tab: string, activeTab: string) =>
  activeTab === tab ? 'page' : undefined;

interface ProgramTabItemProps {
  tab: string;
  activeTab: string;
  onClick: (tab: string) => void;
}

const ProgramTabItem: React.FC<ProgramTabItemProps> = ({
  tab,
  activeTab,
  onClick,
}) => (
  <li
    className={getTabClass(tab, activeTab)}
    onClick={() => onClick(tab)}
    aria-current={getAriaCurrent(tab, activeTab)}>
    <div className="flex items-center gap-x-2">
      <span>{tab}</span>
    </div>
  </li>
);

const ProgramPage: React.FC = () => {
  const {setTitle} = useContext(TitleContext);
  const dispatch = useDispatch<AppDispatch>();
  const params = useParams<{programId?: string}>();
  const programId = params.programId;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);

  const {activeTab, program, status, error} = useSelector(
    (state: RootState) => state.program,
  );
  const faxCoverSheetLoading = useSelector(
    (state: RootState) => state.documentTemplate.status === 'loading',
  );

  const programEntityId = program?.id ? String(program.id) : undefined;
  const hydratedForRoute =
    Boolean(program) && programEntityId === String(programId);
  const showFieldsSkeleton = status === 'loading' || !hydratedForRoute;

  useEffect(() => {
    if (programId) {
      dispatch(ProgramsThunks.show(programId));
    }
  }, [dispatch, programId]);

  useEffect(() => {
    if (activeTab === 'Fax Cover Sheet' && programId) {
      dispatch(DocumentTemplateThunks.getFaxCoverSheetForProgram(programId));
    }
  }, [dispatch, programId, activeTab]);

  useEffect(() => {
    setTitle(
      <>
        {showFieldsSkeleton ? (
          <div>
            <div className="flex items-center space-x-4">
              <Skeleton width={200} height={30} />
            </div>
            <div className="flex items-center space-x-2 text-slate-400 text-sm font-light leading-normal">
              <Skeleton width={120} height={20} />
            </div>
          </div>
        ) : (
          <div>
            <div className="text-slate-600 text-lg font-medium">
              Program details
            </div>
            <div className="text-slate-400 text-sm font-light">
              Manage your program
            </div>
          </div>
        )}
      </>,
    );
  }, [setTitle, showFieldsSkeleton]);

  return (
    <div>
      {status === 'failed' && (
        <div className="p-4 text-red-600">
          Failed to load program{programId ? ` (${programId})` : ''}.{' '}
          {error ?? ''}
        </div>
      )}

      <div key={programId} className="relative overflow-hidden">
        <div className="relative">
          <div className="bg-white border border-slate-200 dark:bg-neutral-800 p-6 rounded-lg shadow">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ProgramField
                label="Name"
                value={program?.name}
                loading={showFieldsSkeleton}
                width={260}
              />
              <ProgramField
                label="Abbreviation"
                value={program?.abbreviationName}
                loading={showFieldsSkeleton}
                width={180}
              />
              <ProgramField
                label="Description"
                value={program?.description}
                loading={showFieldsSkeleton}
                lines={1}
                width={320}
              />
              <ProgramField
                label="Status"
                value={program?.status}
                loading={showFieldsSkeleton}
                width={140}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="p-3"></div>

      {activeTab === 'Billing Solutions' && (
        <div>
          <BillingSolutionTable openDrawer={openDrawer} />
          <Drawer isOpen={isDrawerOpen} onClose={closeDrawer}>
            <BillingSolutionDrawer closeDrawer={closeDrawer} />
          </Drawer>
        </div>
      )}

      {activeTab === 'Custom Data' && (
        <div>
          <DocumentTemplateElementTable openDrawer={openDrawer} />
          <Drawer isOpen={isDrawerOpen} onClose={closeDrawer}>
            <DocumentTemplateElementDrawer closeDrawer={closeDrawer} />
          </Drawer>
        </div>
      )}

      {activeTab === 'Services' && (
        <div>
          <ServicesTable openDrawer={openDrawer} />
          <Drawer isOpen={isDrawerOpen} onClose={closeDrawer}>
            <ServiceDrawer closeDrawer={closeDrawer} />
          </Drawer>
        </div>
      )}

      {activeTab === 'Billing Rules' && (
        <div>
          <BillingRulesTable openDrawer={openDrawer} />
          <Drawer isOpen={isDrawerOpen} onClose={closeDrawer}>
            <BillingRuleDrawer closeDrawer={closeDrawer} />
          </Drawer>
        </div>
      )}

      {activeTab === 'Report Templates' && (
        <div>
          <DocumentTemplateProgramTable openDrawer={openDrawer} />
          <Drawer isOpen={isDrawerOpen} onClose={closeDrawer}>
            <DocumentTemplateProgramDrawer closeDrawer={closeDrawer} />
          </Drawer>
        </div>
      )}

      {activeTab === 'Cards' && (
        <div>
          <ProgramCardsTable />
        </div>
      )}

      {activeTab === 'Fax Cover Sheet' && (
        <div className="p-5 space-y-4 flex flex-col bg-white border border-gray-200 rounded-xl dark:bg-neutral-800 dark:border-neutral-700">
          <div className="text-sm font-medium text-center text-slate-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-700 overflow-x-auto overflow-y-clip">
            <ul className="flex flex-nowrap -mb-px min-w-max">
              {PROGRAM_TABS.map(tab => (
                <ProgramTabItem
                  key={tab}
                  tab={tab}
                  activeTab={activeTab}
                  onClick={tab => dispatch(setActiveTab(tab))}
                />
              ))}
            </ul>
          </div>

          {faxCoverSheetLoading ? (
            <div className="flex flex-col space-y-4">
              <Skeleton height={60} />
              <Skeleton height={400} />
              <Skeleton height={200} />
            </div>
          ) : (
            <DocumentTemplateFormPage />
          )}
        </div>
      )}
    </div>
  );
};

export default ProgramPage;
